import axios, { AxiosInstance } from 'axios';

// Create connection to backend
const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5001').replace(/\/$/, '') + '/api/';

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to every request (if logged in)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auto-logout on 401 (token expired or invalid)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Authentication API calls
export const authAPI = {
  register: (email: string, password: string, full_name: string, role: string) =>
    api.post('auth/register', { email, password, full_name, role }),

  login: (email: string, password: string) =>
    api.post('auth/login', { email, password }),

  getProfile: () => api.get('auth/me'),
};

// Timesheet API calls
export const timesheetAPI = {
  // Create new timesheet entry
  createTimesheet: (data: {
    engagement_id?: string;
    date: string;
    hours: number;
    category: string;
    service_type?: string;
    task_activity?: string;
    notes?: string;
  }) => api.post('timesheets', data),

  // Get my timesheets
  getMyTimesheets: (startDate?: string, endDate?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return api.get(`timesheets/my/list?${params.toString()}`);
  },

  // Get single timesheet
  getTimesheet: (id: string) => api.get(`timesheets/${id}`),

  // Update timesheet
  updateTimesheet: (id: string, data: any) => api.patch(`timesheets/${id}`, data),

  // Submit timesheet for approval
  submitTimesheet: (id: string) => api.post(`timesheets/${id}/submit`),

  // Get team timesheets (manager view)
  getTeamTimesheets: (month?: string) => {
    const params = month ? `?month=${month}` : '';
    return api.get(`timesheets/team/list${params}`);
  },

  // Get pending non-chargeable timesheets (Partner view)
  getPendingNonChargeableTimesheets: (month?: string) => {
    const params = month ? `?month=${month}` : '';
    return api.get(`timesheets/pending/non-chargeable${params}`);
  },

  // Approve non-chargeable timesheet
  approveNonChargeableTimesheet: (id: string, notes?: string) =>
    api.patch(`timesheets/${id}/approve-non-chargeable`, { notes }),

  // Reject non-chargeable timesheet
  rejectNonChargeableTimesheet: (id: string, notes?: string) =>
    api.patch(`timesheets/${id}/reject-non-chargeable`, { notes }),

  // Get daily hours total for a given date
  getDailyHours: (date: string) => api.get(`timesheets/daily-hours?date=${date}`),

  // Delete a timesheet entry (used by matrix to clear a cell)
  deleteTimesheet: (id: string) => api.delete(`timesheets/${id}`),
};

// Partner Dashboard API calls
export const partnerAPI = {
  // Get partner dashboard (WIP, budget, alerts)
  getDashboard: () => api.get('partner/dashboard'),

  // Get partner's engagements
  getEngagements: () => api.get('partner/engagements'),

  // Create new engagement
  createEngagement: (data: any) => api.post('partner/engagements', data),
};

// Billing API calls
export const billingAPI = {
  // Get billing instructions (with optional status/engagement_id filter)
  getInstructions: (status?: string, engagement_id?: string) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (engagement_id) params.append('engagement_id', engagement_id);
    const qs = params.toString();
    return api.get(`billing/instructions${qs ? '?' + qs : ''}`);
  },

  // Create draft billing instruction (legacy)
  createDraftInstruction: (engagement_id: string, billing_type: string) =>
    api.post('billing/instructions/draft', { engagement_id, billing_type }),

  // Update draft instruction (legacy)
  updateDraftInstruction: (id: string, data: any) =>
    api.patch(`billing/instructions/${id}`, data),

  // Issue instruction with gap disposition (legacy)
  issueInstruction: (id: string, gap_disposition: string, disposition_reason: string) =>
    api.patch(`billing/instructions/${id}/issue`, { gap_disposition, disposition_reason }),

  // Confirm instruction in QB (legacy Admin)
  confirmInstructionLegacy: (id: string, qb_invoice_number: string, qb_actual_amount: number) =>
    api.patch(`billing/instructions/${id}/confirm`, { qb_invoice_number, qb_actual_amount }),
};

// New Billing Control API
export const billingControlAPI = {
  // Get engagement summary for master table
  getControlSummary: () => api.get('billing/control'),

  // Get instruction history for a specific engagement
  getInstructionHistory: (engagement_id: string, status?: string) => {
    const params = new URLSearchParams({ engagement_id });
    if (status) params.append('status', status);
    return api.get(`billing/instructions?${params.toString()}`);
  },

  // Create a new instruction (directly Issued)
  createInstruction: (data: { engagement_id: string; amount: number; notes?: string }) =>
    api.post('billing/instructions', data),

  // Confirm an instruction (Partner/Manager) — optionally record invoice reference and amount
  confirmInstruction: (id: string, invoice_reference?: string, amount_invoiced?: number) =>
    api.put(`billing/instructions/${id}/confirm`, { invoice_reference, amount_invoiced }),

  // Reject an instruction with reason
  rejectInstruction: (id: string, rejection_reason: string) =>
    api.put(`billing/instructions/${id}/reject`, { rejection_reason }),

  // Update engagement WIP Value
  updateWipValue: (engagement_id: string, wip_value: number) =>
    api.put(`billing/engagements/${engagement_id}/wip-value`, { wip_value }),

  // Update engagement Agreed Price (Partner only)
  updateAgreedPrice: (engagement_id: string, data: {
    base_price: number;
    discount?: number;
    additional_works?: number;
    oop?: number;
    variation_order?: number;
  }) => api.put(`billing/engagements/${engagement_id}/agreed-price`, data),

  // Admin queue
  getAdminQueue: () => api.get('billing/admin/queue'),
  adminComplete: (id: string, referenceInvoiceNumber?: string) =>
    api.put(`billing/admin/queue/${id}/complete`, { referenceInvoiceNumber }),

  // Recurring billing pre-drafts
  getRecurringPending: () => api.get('billing/recurring/pending'),
  approveRecurringDraft: (id: string) => api.post(`billing/recurring/approve/${id}`),
  deferRecurringDraft: (id: string, data: { deferralReason: string; deferToPeriod?: string }) =>
    api.post(`billing/recurring/defer/${id}`, data),
  generateRecurringDrafts: (period?: string, engagementId?: string) =>
    api.post('billing/recurring/generate', {
      ...(period ? { period } : {}),
      ...(engagementId ? { engagementId } : {}),
    }),
};

// Receivables API
export const receivablesAPI = {
  upload: (csvContent: string) => api.post('receivables/upload', { csvContent }),
  getSummary: (includeProforma: boolean) => api.get(`receivables/summary?includeProforma=${includeProforma}`),
  getReceivables: (status: string = 'Outstanding') => api.get(`receivables?status=${status}`),
  instruct: (id: string, actionType: string, note: string) =>
    api.post(`receivables/${id}/instruct`, { action_type: actionType, note }),
  getAdminQueue: () => api.get('receivables/admin/queue'),
  completeInstruction: (id: string) => api.put(`receivables/admin/queue/${id}/complete`),
};

// Recovery Rate API calls
export const recoveryAPI = {
  jobs: (status: 'open' | 'closed' | 'all' = 'all') =>
    api.get(`recovery/jobs?status=${status}`),
  job: (id: string) => api.get(`recovery/jobs/${id}`),
  person: (staffId: string, period: string, status: 'all' | 'open' | 'closed' = 'all') =>
    api.get(`recovery/person/${staffId}?period=${period}&status=${status}`),
  peer: (grade: string, period: string, status: 'all' | 'open' | 'closed' = 'all') =>
    api.get(`recovery/peer?grade=${encodeURIComponent(grade)}&period=${period}&status=${status}`),
  people: (period: string, status: 'all' | 'open' | 'closed' = 'all') =>
    api.get(`recovery/people?period=${period}&status=${status}`),
  multipliers: () => api.get('recovery/multipliers'),
  setMultiplier: (grade: string, multiplier: number) =>
    api.put(`recovery/multipliers/${grade}`, { multiplier }),
};

// Period Close API calls
export const periodCloseAPI = {
  // Get period close report
  getPeriodReport: (period: string) => api.get(`period-close?period=${period}`),

  // Sign off period (irreversible)
  signOffPeriod: (period: string) => api.post(`period-close/${period}/sign-off`),

  // Year-end close
  getYearEndStatus: () => api.get('period-close/year-end/status'),
  closeYear: (fiscalYear?: string) => api.post('period-close/year-end/close', fiscalYear ? { fiscalYear } : {}),
  getReportsArchive: () => api.get('period-close/year-end/reports'),
  getYearReport: (reportId: string) => api.get(`period-close/year-end/reports/${reportId}`),
};

// Notification API calls
export const notificationAPI = {
  // Get pending timesheet reminders
  getTimesheetReminders: () => api.get('notifications/timesheet-reminders'),

  // Get pending deadline alerts
  getDeadlineAlerts: () => api.get('notifications/deadline-alerts'),

  // Get pending budget alerts
  getBudgetAlerts: () => api.get('notifications/budget-alerts'),

  // Manually trigger all notifications
  processNotifications: () => api.post('notifications/process'),
};

// Manager API calls
export const managerAPI = {
  // Get team timesheets summary
  getTeamList: (month?: string) => {
    const params = month ? `?month=${month}` : '';
    return api.get(`timesheets/team/list${params}`);
  },

  // Get specific team member's timesheet
  getTeamMember: (memberId: string, month?: string) => {
    const params = month ? `?month=${month}` : '';
    return api.get(`timesheets/team/${memberId}${params}`);
  },

  // Approve timesheet
  approveTimesheet: (timesheetId: string) =>
    api.patch(`timesheets/${timesheetId}/approve`),

  // Reject timesheet
  rejectTimesheet: (timesheetId: string, rejectionReason?: string) =>
    api.patch(`timesheets/${timesheetId}/reject`, { rejectionReason }),
};

// Engagement API calls
export const engagementAPI = {
  // Get my engagements (Staff view)
  getMyEngagements: () => api.get('engagements/my'),

  // Get all engagements (Manager/Partner view)
  getAllEngagements: () => api.get('engagements'),

  // Create new engagement
  createEngagement: (data: any) =>
    api.post('engagements', data),

  // Update engagement
  updateEngagement: (engagementId: string, data: any) =>
    api.patch(`engagements/${engagementId}`, data),

  // Get assigned staff for engagement
  getEngagementStaff: (engagementId: string) =>
    api.get(`engagements/${engagementId}/staff`),

  // Get the month-by-month billed/pending schedule for a recurring engagement
  getRecurringSchedule: (engagementId: string) =>
    api.get(`engagements/${engagementId}/recurring-schedule`),

  // Add staff to engagement
  assignStaff: (engagementId: string, staffId: string, budgetHours: number) =>
    api.post(`engagements/${engagementId}/staff`, { staffId, budgetHours }),

  // Update staff budget hours
  updateStaffBudgetHours: (engagementId: string, staffId: string, budgetHours: number) =>
    api.patch(`engagements/${engagementId}/staff/${staffId}`, { budgetHours }),

  // Remove staff from engagement
  removeStaff: (engagementId: string, staffId: string) =>
    api.delete(`engagements/${engagementId}/staff/${staffId}`),

  // Delete duplicate engagement (Partner only; blocked if time or billing exists)
  deleteEngagement: (engagementId: string) =>
    api.delete(`engagements/${engagementId}`),

  // Get bid pipeline
  getBidPipeline: () => api.get('engagements/bid-pipeline'),

  // Create bid
  createBid: (data: any) =>
    api.post('engagements/bid-pipeline', data),

  // Update bid
  updateBid: (bidId: string, data: any) =>
    api.patch(`engagements/bid-pipeline/${bidId}`, data),

  // Delete bid (Partner only)
  deleteBid: (bidId: string) =>
    api.delete(`engagements/bid-pipeline/${bidId}`),

  // Mark engagement as Completed / Force-Close (Partner only)
  completeEngagement: (engagementId: string, data: { forceClose?: boolean; pendingWipAmount?: number }) =>
    api.post(`engagements/${engagementId}/complete`, data),

  // Update Next Due Critical field (Partner only)
  updateNextDueCritical: (engagementId: string, data: { nextDueCritical?: string | null; nextDueCriticalDate?: string | null }) =>
    api.patch(`engagements/${engagementId}/next-due-critical`, data),

  // Confirm a flagged engagement (maker-checker)
  confirmEngagement: (id: string) => api.patch(`engagements/${id}/confirm`),

  // Reject a flagged engagement's confirmation, with reason
  rejectEngagementConfirmation: (id: string, reason: string) =>
    api.patch(`engagements/${id}/reject-confirmation`, { reason }),
};

// Admin API calls (User Management)
export const adminAPI = {
  // List all users
  listUsers: () => api.get('admin/users'),

  // Create new user
  createUser: (data: { email: string; password: string; full_name: string; role: string; grade?: string | null }) =>
    api.post('admin/users', data),

  // Update user (full_name, role, status, approver_id, grade)
  updateUser: (userId: string, data: Partial<{ full_name: string; role: string; status: string; approver_id: string | null; grade: string | null }>) =>
    api.patch(`admin/users/${userId}`, data),

  // Approve a pending user (set role + approver + activate)
  approveUser: (userId: string, role: string, approver_id: string | null) =>
    api.patch(`admin/users/${userId}/approve`, { role, approver_id }),

  // Deactivate user
  deactivateUser: (userId: string) =>
    api.patch(`admin/users/${userId}/deactivate`),

  // Reactivate user
  reactivateUser: (userId: string) =>
    api.patch(`admin/users/${userId}/reactivate`),

  // Reset user password
  resetPassword: (userId: string, newPassword: string) =>
    api.patch(`admin/users/${userId}/reset-password`, { newPassword }),

  // Confirm a flagged user (maker-checker)
  confirmUser: (userId: string) => api.patch(`admin/users/${userId}/confirm`),

  // Reject a flagged user's confirmation, with reason
  rejectUserConfirmation: (userId: string, reason: string) =>
    api.patch(`admin/users/${userId}/reject-confirmation`, { reason }),
};

// Non-Chargeable Types API
export const nonChargeableTypeAPI = {
  // List all non-chargeable types
  listTypes: () => api.get('non-chargeable-types'),

  // Create new type
  createType: (data: { name: string; description?: string; requiresApproval: boolean }) =>
    api.post('non-chargeable-types', data),

  // Update type
  updateType: (typeId: string, data: Partial<{ name: string; description: string; requiresApproval: boolean }>) =>
    api.patch(`non-chargeable-types/${typeId}`, data),

  // Delete type
  deleteType: (typeId: string) =>
    api.delete(`non-chargeable-types/${typeId}`),
};

// Expense API
export const expenseAPI = {
  logExpense: (data: { engagement_id: string; description: string; amount: number; category?: string; timesheet_id?: string }) =>
    api.post('expenses', data),

  getMyExpenses: () => api.get('expenses/my'),
};

export default api;
